// React.lazy that survives a deploy.
//
// A tab left open across a deploy still points at the OLD build's lazy chunks
// (Hosts & Vendors, Groups, Your Stories, every modal), and Netlify stops
// serving those the moment a new build goes live. Opening one of those pages
// then rejected inside React.lazy with nothing to catch it, and React cleared
// the whole screen (Andrew, 2026-09-25, clicking Hosts & Vendors from the
// calendar after three deploys that evening). Reproduced locally by deleting a
// built chunk: "Failed to fetch dynamically imported module", then white.
//
// Vite's own vite:preloadError event does NOT cover this: it only fires for
// imports routed through its preload helper, which a chunk with no CSS or
// shared deps skips. So the catch lives here, on the import itself.
//
// A reload fetches the current build, and since the address bar already shows
// the page they asked for, it lands them on it. Once per 10 seconds, so a chunk
// that is broken on the CURRENT build cannot put the tab in a reload loop; the
// second failure throws as before.

import { lazy, type ComponentType } from 'react'
import { track } from './analytics'

const KEY = 'lyh_chunk_reload_at'

function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) || 0)
    if (Date.now() - last < 10_000) return false
    sessionStorage.setItem(KEY, String(Date.now()))
  } catch {
    // No sessionStorage (some private modes): still reload, the guard is a nicety.
  }
  track('stale_chunk_reload', { path: window.location.pathname })
  window.location.reload()
  return true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    load().catch((error: unknown) => {
      // Never resolves: the Suspense fallback stays up until the reload lands.
      if (reloadOnce()) return new Promise<{ default: T }>(() => {})
      throw error
    }),
  )
}
